import {spawnSuccessToast, spawnErrorToast} from '@hm/fundamental'
import {QueryClient, useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {getActivePersona, getPersonas, selectPersona} from '../../api/endpoints'
import {Persona} from '../../api/types/personas'
import {ROUTES} from '../routing/routes'

const invalidatePersonaQueries = async (queryClient: QueryClient) => {
  await Promise.all([
    queryClient.invalidateQueries({queryKey: ['featuresMenu']}),
    queryClient.invalidateQueries({queryKey: ['activePersona']})
  ])
}

interface UsePersonaMutationOptions {
  errorMessageKey: string
  successMessageKey: string
  onSuccess?: () => void
}

const usePersonaMutation = ({
  errorMessageKey,
  successMessageKey,
  onSuccess
}: UsePersonaMutationOptions) => {
  const {t} = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (personaId: number) => {
      await selectPersona(personaId)
    },
    onError: () => spawnErrorToast(t(errorMessageKey)),
    onSuccess: async () => {
      await invalidatePersonaQueries(queryClient)
      spawnSuccessToast(t(successMessageKey))
      onSuccess?.()
    }
  })
}

export const usePersonas = () => {
  const {data: personas, isLoading} = useQuery({
    queryKey: ['personas'],
    queryFn: async () => {
      const {data} = await getPersonas()
      return data
    },
    staleTime: Infinity
  })

  return {
    personas,
    isLoading
  }
}

export const useActivePersona = () => {
  const {data: activePersona, isLoading} = useQuery<Persona | undefined>({
    queryKey: ['activePersona'],
    queryFn: async () => {
      const {data, status} = await getActivePersona()
      return status === 204 ? undefined : data
    },
    staleTime: Infinity
  })

  return {
    activePersona,
    isLoading
  }
}

export const useSelectPersona = () => {
  return usePersonaMutation({
    errorMessageKey: 'suite.error.selectPersona',
    successMessageKey: 'suite.success.selectPersona'
  })
}

export const useSwitchPersona = () => {
  const navigate = useNavigate()

  return usePersonaMutation({
    errorMessageKey: 'suite.error.switchPersona',
    successMessageKey: 'suite.success.switchPersona',
    onSuccess: () => {
      void navigate(ROUTES.path)
    }
  })
}
